import { Button, Checkbox, NumberInput, Textarea, TextInput } from '@mantine/core';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type FormField, isValidFieldName, type OnFail, regexError } from '@/lib/form-specs';
import { kindLabel } from './FieldCanvas';
import classes from './FieldEditor.module.css';
import { capabilities, linesFromList, listFromLines } from './model';

const RULES: OnFail[] = ['reject', 'spam', 'trash', 'pass'];

const toBound = (value: string | number) => (typeof value === 'number' ? value : undefined);

function NameInput({
  value,
  taken,
  onRename,
}: {
  value: string;
  taken: Set<string>;
  onRename: (name: string) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  const error =
    draft === ''
      ? t('fields.fieldNameEmpty')
      : !isValidFieldName(draft)
        ? t('fields.fieldNameInvalid')
        : draft !== value && taken.has(draft)
          ? t('fields.fieldNameTaken')
          : null;

  const commit = () => {
    if (!error && draft !== value) onRename(draft);
  };

  return (
    <TextInput
      label={t('fields.fieldName')}
      description={t('fields.fieldNameHelp')}
      inputWrapperOrder={['label', 'input', 'description', 'error']}
      classNames={{ input: classes.mono }}
      value={draft}
      error={error}
      onChange={(e) => setDraft(e.currentTarget.value.trim())}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
      }}
      spellCheck={false}
    />
  );
}

/** One entry per line; keeps the raw text locally so a trailing newline survives typing. */
function ListInput({
  label,
  description,
  value,
  onChange,
  error,
}: {
  label: string;
  description: string;
  value?: string;
  onChange: (list: string) => void;
  error?: string | null;
}) {
  const [draft, setDraft] = useState(() => linesFromList(value));
  return (
    <Textarea
      label={label}
      description={description}
      inputWrapperOrder={['label', 'input', 'description', 'error']}
      autosize
      minRows={3}
      value={draft}
      error={error}
      onChange={(e) => {
        setDraft(e.currentTarget.value);
        onChange(listFromLines(e.currentTarget.value));
      }}
    />
  );
}

export interface FieldPropertiesPanelProps {
  fieldKey: string;
  field: FormField;
  takenNames: Set<string>;
  onPatch: (patch: Partial<FormField>) => void;
  onRename: (name: string) => void;
  onDuplicate: () => void;
  onRemove: () => void;
}

export function FieldPropertiesPanel({
  fieldKey,
  field,
  takenNames,
  onPatch,
  onRename,
  onDuplicate,
  onRemove,
}: FieldPropertiesPanelProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const ruleName = useId();
  const caps = capabilities(field);
  const rule = field.on_fail ?? 'pass';
  const rules = caps.trap ? RULES.filter((r) => r !== 'pass') : RULES;
  const patternError = field.is_pattern ? regexError(field.is_pattern) : null;
  const rangeError =
    field.is_min !== undefined && field.is_max !== undefined && field.is_min > field.is_max
      ? t('fields.rangeInvalid')
      : null;

  return (
    <aside className={`${classes.panel} ${classes.props}`} aria-labelledby={headingId}>
      <div className={classes.propsHead}>
        <h2 id={headingId} className={classes.propsTitle}>
          {caps.trap
            ? t('fields.typeTag.trap')
            : t('fields.propsTitle', { type: kindLabel(t, field) })}
        </h2>
        <div>
          <Button variant="subtle" size="xs" onClick={onDuplicate}>
            {t('fields.duplicate')}
          </Button>
          <Button variant="subtle" size="xs" c="var(--fs-risk-text)" onClick={onRemove}>
            {t('fields.remove')}
          </Button>
        </div>
      </div>

      {caps.label && (
        <TextInput
          label={t('fields.label')}
          value={field.label ?? ''}
          onChange={(e) => onPatch({ label: e.currentTarget.value })}
        />
      )}

      <NameInput key={fieldKey} value={field.name} taken={takenNames} onRename={onRename} />

      {caps.trap && <div className={classes.note}>{t('fields.trapNote')}</div>}

      {caps.value && (
        <TextInput
          label={t('fields.value')}
          value={field.value === undefined ? '' : String(field.value)}
          onChange={(e) => onPatch({ value: e.currentTarget.value })}
        />
      )}

      {caps.placeholder && (
        <TextInput
          label={t('fields.placeholder')}
          value={field.placeholder ?? ''}
          onChange={(e) => onPatch({ placeholder: e.currentTarget.value })}
        />
      )}
      {caps.help && (
        <TextInput
          label={t('fields.help')}
          value={field.helptext ?? ''}
          onChange={(e) => onPatch({ helptext: e.currentTarget.value })}
        />
      )}

      {caps.options && (
        <ListInput
          key={`${fieldKey}-options`}
          label={t('fields.options')}
          description={t('fields.optionsHelp')}
          value={field.options}
          error={field.options ? null : t('fields.optionsMissing')}
          onChange={(options) => onPatch({ options })}
        />
      )}

      {!caps.trap && (
        <div className={classes.block}>
          <strong>{t('fields.checks')}</strong>
          <Checkbox
            label={t('fields.required')}
            checked={field.required === true}
            onChange={(e) => onPatch({ required: e.currentTarget.checked })}
          />
          {field.required && rule === 'pass' && (
            <span className={classes.rh}>{t('fields.requiredButKept')}</span>
          )}
          {caps.email && (
            <Checkbox
              label={t('fields.isEmail')}
              checked={field.is_email === true}
              onChange={(e) => onPatch({ is_email: e.currentTarget.checked })}
            />
          )}
          {caps.url && (
            <Checkbox
              label={t('fields.isUrl')}
              checked={field.is_url === true}
              onChange={(e) => onPatch({ is_url: e.currentTarget.checked })}
            />
          )}
          {(caps.lengthBounds || caps.valueBounds) && (
            <div>
              <div className={classes.grid2}>
                <NumberInput
                  label={caps.valueBounds ? t('fields.lowest') : t('fields.shortest')}
                  description={caps.lengthBounds ? t('fields.characters') : undefined}
                  inputWrapperOrder={['label', 'input', 'description']}
                  min={0}
                  allowNegative={false}
                  allowDecimal={false}
                  value={field.is_min ?? ''}
                  onChange={(v) => onPatch({ is_min: toBound(v) })}
                />
                <NumberInput
                  label={caps.valueBounds ? t('fields.highest') : t('fields.longest')}
                  description={caps.lengthBounds ? t('fields.characters') : undefined}
                  inputWrapperOrder={['label', 'input', 'description']}
                  min={0}
                  allowNegative={false}
                  allowDecimal={false}
                  value={field.is_max ?? ''}
                  onChange={(v) => onPatch({ is_max: toBound(v) })}
                />
              </div>
              {rangeError && <span className={classes.error}>{rangeError}</span>}
            </div>
          )}
          {caps.pattern && (
            <TextInput
              label={
                <>
                  {t('fields.pattern')}{' '}
                  <span className={classes.sub} style={{ fontWeight: 400 }}>
                    {t('fields.patternOptional')}
                  </span>
                </>
              }
              classNames={{ input: classes.mono }}
              placeholder="^[A-Za-z ]+$"
              spellCheck={false}
              value={field.is_pattern ?? ''}
              error={patternError ? t('fields.patternInvalid', { error: patternError }) : null}
              onChange={(e) => onPatch({ is_pattern: e.currentTarget.value })}
            />
          )}
          {caps.allowedValues && (
            <ListInput
              key={`${fieldKey}-is_in`}
              label={t('fields.allowedValues')}
              description={t('fields.allowedValuesHelp')}
              value={field.is_in}
              onChange={(is_in) => onPatch({ is_in })}
            />
          )}
          {caps.multiple && (
            <Checkbox
              label={field.field === 'file' ? t('fields.multipleFile') : t('fields.multipleChoice')}
              checked={field.multiple === true}
              onChange={(e) => onPatch({ multiple: e.currentTarget.checked })}
            />
          )}
        </div>
      )}

      <fieldset className={classes.fieldset}>
        <legend className={classes.legend}>
          {caps.trap ? t('fields.ruleLegendTrap') : t('fields.ruleLegend')}
        </legend>
        <span className={classes.rh}>{t('fields.ruleHelp')}</span>
        {rules.map((r) => (
          <label key={r} className={`${classes.opt} ${rule === r ? classes.optOn : ''}`}>
            <input
              type="radio"
              name={ruleName}
              checked={rule === r}
              onChange={() => onPatch({ on_fail: r === 'pass' ? undefined : r })}
            />
            <strong>{t(`fields.ruleOption.${r}.title`)}</strong>
            <span className={`${classes.rh} ${classes.optText}`}>
              {t(`fields.ruleOption.${r}.text`)}
            </span>
          </label>
        ))}
      </fieldset>

      {!caps.trap && (
        <details className={classes.details}>
          <summary>{t('fields.more')}</summary>
          <div className={classes.detailsBody}>
            <TextInput
              label={t('fields.storeAs')}
              placeholder={t('fields.storeAsPlaceholder')}
              classNames={{ input: classes.mono }}
              spellCheck={false}
              value={field.map_to ?? ''}
              onChange={(e) => onPatch({ map_to: e.currentTarget.value.trim() })}
            />
            <Checkbox
              label={t('fields.readonly')}
              checked={field.readonly === true}
              onChange={(e) => onPatch({ readonly: e.currentTarget.checked })}
            />
            <Checkbox
              label={t('fields.disabled')}
              checked={field.disabled === true}
              onChange={(e) => onPatch({ disabled: e.currentTarget.checked })}
            />
            {field.field !== 'hidden' && (
              <Checkbox
                label={t('fields.hidden')}
                checked={field.hidden === true}
                onChange={(e) => onPatch({ hidden: e.currentTarget.checked })}
              />
            )}
            <Checkbox
              label={t('fields.isEmpty')}
              checked={field.is_empty === true}
              onChange={(e) => onPatch({ is_empty: e.currentTarget.checked })}
            />
            <Checkbox
              label={t('fields.discard')}
              checked={field.discard === true}
              onChange={(e) => onPatch({ discard: e.currentTarget.checked })}
            />
          </div>
        </details>
      )}
    </aside>
  );
}
